//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import DOMPurify from 'dompurify';
import { isSafeRedirectUrl } from './validateUrlUtils';

/**
 * Security utilities for preventing common web vulnerabilities
 * Includes XSS prevention, URL validation, and sensitive data masking
 */

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param html - Raw HTML string that may contain malicious content
 * @returns Sanitized HTML safe for rendering
 */
export const sanitizeHtmlContent = (
  html: string | null | undefined,
): string => {
  if (!html) return '';
  try {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'b',
        'i',
        'em',
        'strong',
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'a',
        'code',
        'pre',
        'blockquote',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
      ],
      ALLOWED_ATTR: ['href', 'title', 'target'],
      KEEP_CONTENT: true,
    });
  } catch {
    console.error('Error sanitizing HTML content');
    return '';
  }
};

/**
 * Sanitize a URL before using it for redirects or navigation
 * @param url - URL to validate
 * @returns True if URL is safe to use, false otherwise
 */
export const sanitizeRedirectUrl = (
  url: string | null | undefined,
): boolean => {
  if (!url) return false;
  return isSafeRedirectUrl(url);
};

/**
 * Extract sensitive keys that should not be logged
 */
const SENSITIVE_KEYS = [
  'password',
  'token',
  'secret',
  'authorization',
  'auth',
  'api_key',
  'apikey',
  'access_token',
  'refresh_token',
  'client_secret',
  'oauth_client_secret',
  'credentials',
  'private_key',
  'privatekeyheader',
];

/**
 * Mask sensitive data in objects to prevent accidental logging
 * @param obj - Object that may contain sensitive data
 * @param depth - Recursion depth to prevent infinite loops
 * @param seen - WeakSet to track visited objects and prevent circular references
 * @returns Object with sensitive fields redacted
 */
export const maskSensitiveData = (
  obj: any,
  depth = 0,
  seen = new WeakSet(),
): any => {
  if (depth > 10) return {};
  if (!obj || typeof obj !== 'object') return obj;
  if (seen.has(obj)) return '[Circular]';
  seen.add(obj);

  if (Array.isArray(obj)) {
    return obj.map((item) => maskSensitiveData(item, depth + 1, seen));
  }

  const masked: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    if (
      SENSITIVE_KEYS.some((sensitiveKey) => lowerKey.includes(sensitiveKey))
    ) {
      masked[key] = '[REDACTED]';
    } else if (value && typeof value === 'object') {
      masked[key] = maskSensitiveData(value, depth + 1, seen);
    } else {
      masked[key] = value;
    }
  }
  return masked;
};

/**
 * Create a safe console logger that masks sensitive data
 * Logs and warnings are only shown in development mode
 * Errors are always logged to enable production debugging
 */
export const createSecureLogger = (namespace: string) => {
  const isDev = process.env.NODE_ENV === 'development';

  return {
    log: (...args: any[]) => {
      if (isDev) {
        const maskedArgs = args.map((arg) =>
          typeof arg === 'object' && arg !== null
            ? maskSensitiveData(arg)
            : arg,
        );
        console.log(`[${namespace}]`, ...maskedArgs);
      }
    },
    error: (...args: any[]) => {
      const maskedArgs = args.map((arg) =>
        typeof arg === 'object' && arg !== null ? maskSensitiveData(arg) : arg,
      );
      console.error(`[${namespace}]`, ...maskedArgs);
    },
    warn: (...args: any[]) => {
      if (isDev) {
        const maskedArgs = args.map((arg) =>
          typeof arg === 'object' && arg !== null
            ? maskSensitiveData(arg)
            : arg,
        );
        console.warn(`[${namespace}]`, ...maskedArgs);
      }
    },
  };
};

/**
 * Validate and escape content before rendering in React components
 * Safe for use with dangerouslySetInnerHTML when content has been sanitized
 * @param html - HTML content to validate
 * @returns Object safe to pass to dangerouslySetInnerHTML or empty string
 */
export const renderSafeHtml = (html: string | null | undefined) => {
  const sanitized = sanitizeHtmlContent(html);
  return { __html: sanitized };
};

/**
 * Escape HTML special characters to prevent XSS via text content
 * @param text - Text to escape
 * @returns Escaped text safe for HTML rendering
 */
export const escapeHtml = (text: string | null | undefined): string => {
  if (!text) return '';
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
};

/**
 * Safely set a property on an object, preventing prototype pollution.
 * Rejects keys like __proto__, constructor, and prototype.
 * @param obj - Target object
 * @param key - Property key to set
 * @param value - Value to assign
 * @returns true if the property was set, false if rejected
 */
export const safeSetProperty = (
  obj: Record<string, any>,
  key: string,
  value: any,
): boolean => {
  if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
    return false;
  }
  obj[key] = value;
  return true;
};

/**
 * Safely parse JSON, returning a fallback value on failure.
 * Prevents injection via malformed JSON payloads.
 * @param text - JSON string to parse
 * @param fallback - Value to return on parse failure (default: null)
 * @returns Parsed value or fallback
 */
export const safeJsonParse = <T = any>(
  text: string | null | undefined,
  fallback: T | null = null,
): T | null => {
  if (!text) return fallback;
  try {
    return JSON.parse(text) as T;
  } catch {
    console.error('safeJsonParse: Failed to parse JSON input');
    return fallback;
  }
};

/**
 * Sanitize a filename to prevent path traversal and injection.
 * Only allows alphanumeric, dash, underscore, dot characters.
 * @param filename - Raw filename string
 * @returns Sanitized filename safe for download attributes
 */
export const sanitizeFilename = (
  filename: string | null | undefined,
): string => {
  if (!filename) return 'download';
  // Remove path separators and null bytes
  const cleaned = filename.replace(/[/\\:\0]/g, '');
  // Only keep safe characters
  return cleaned.replace(/[^a-zA-Z0-9._\- ]/g, '_') || 'download';
};

/**
 * Safely navigate to a validated same-origin URL.
 * Breaks taint chain by validating and reconstructing the URL before assignment.
 * @param url - URL to navigate to
 * @param fallback - Fallback path if URL is invalid (default: '/')
 */
export const safeNavigate = (
  url: string | null | undefined,
  fallback = '/',
): void => {
  if (!url || !isSafeRedirectUrl(url)) {
    window.location.href = fallback;
    return;
  }
  // Reconstruct URL to break any potential injection
  const validated = new URL(url, window.location.origin);
  if (validated.origin !== window.location.origin) {
    window.location.href = fallback;
    return;
  }
  // Break taint chain by reconstructing from validated components (CWE-601)
  const safeUrl = `${validated.origin}${validated.pathname}${validated.search}${validated.hash}`;
  window.location.href = safeUrl;
};

/**
 * Safely navigate the top window to a validated same-origin URL.
 * @param url - URL to navigate to
 * @param fallback - Fallback path if URL is invalid (default: '/')
 */
export const safeNavigateTop = (
  url: string | null | undefined,
  fallback = '/',
): void => {
  if (!url || !isSafeRedirectUrl(url)) {
    if (window.top) window.top.location.href = fallback;
    return;
  }
  const validated = new URL(url, window.location.origin);
  if (validated.origin !== window.location.origin) {
    if (window.top) window.top.location.href = fallback;
    return;
  }
  // Break taint chain by reconstructing from validated components (CWE-601)
  const safeUrl = `${validated.origin}${validated.pathname}${validated.search}${validated.hash}`;
  if (window.top) window.top.location.href = safeUrl;
};

/**
 * Safely navigate a popup/window to a validated OAuth URL (HTTPS only).
 * Breaks taint chain by reconstructing the URL after validation.
 * @param target - Window or popup to navigate
 * @param url - URL to navigate to
 * @returns true if navigation was performed, false otherwise
 */
export const safeOAuthNavigate = (
  target: Window | null,
  url: string | null | undefined,
): boolean => {
  if (!target || target.closed) return false;
  if (!url) return false;
  // Reject protocol-relative URLs
  if (url.startsWith('//')) return false;
  try {
    const validated = new URL(url);
    if (validated.protocol !== 'https:') return false;
    // Reconstruct to break taint chain
    target.location.href = validated.toString();
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate and sanitize a URL for use as an iframe src.
 * Allows same-origin URLs by default. Additional trusted origins can be
 * supplied via the allowedOrigins parameter to support cross-origin
 * dashboard embeds.
 * @param url - URL to validate
 * @param allowedOrigins - Optional set of trusted origins (e.g. dashboard hosts)
 * @returns Validated URL string or 'about:blank'
 */
export const sanitizeIframeSrc = (
  url: string | null | undefined,
  allowedOrigins?: string[],
): string => {
  if (!url) return 'about:blank';
  try {
    const validated = new URL(url, window.location.origin);
    const trusted = new Set([
      window.location.origin,
      ...(allowedOrigins ?? []),
    ]);
    if (!trusted.has(validated.origin)) return 'about:blank';
    // Reject dangerous schemes even if origin matched
    if (validated.protocol !== 'https:' && validated.protocol !== 'http:') {
      return 'about:blank';
    }
    return validated.toString();
  } catch {
    return 'about:blank';
  }
};

/**
 * Safely read and validate the base path from the document's <base> tag.
 * Returns a validated path string that is safe from DOM-sourced XSS.
 * @returns Validated base path string
 */
export const getSanitizedBasePath = (): string => {
  const raw =
    document.querySelector('head > base')?.getAttribute('href') ?? '/';
  // Only allow simple path patterns (no protocol, no host)
  if (
    raw.includes('://') ||
    raw.startsWith('//') ||
    raw.includes('\0') ||
    raw.includes('<')
  ) {
    return '/';
  }
  // Validate it's a path-like string
  const cleaned = raw.replace(/[^a-zA-Z0-9/_\-.~%]/g, '');
  if (!cleaned.startsWith('/')) return '/';
  // Remove trailing slash unless it's just "/"
  if (cleaned !== '/' && cleaned.endsWith('/')) {
    return cleaned.slice(0, -1);
  }
  return cleaned;
};

/**
 * Check whether a dynamic key is safe to use as an object property.
 * Rejects prototype-polluting keys like __proto__, constructor, prototype.
 * Use this for validation-only checks without side effects.
 * @param key - Property key to validate
 * @returns true if the key is safe, false otherwise
 */
export const isSafeKey = (key: string): boolean => {
  return (
    !!key && key !== '__proto__' && key !== 'constructor' && key !== 'prototype'
  );
};

/**
 * Safely assign a dynamic key to a plain object, preventing prototype pollution.
 * Uses Object.defineProperty with explicit property descriptor to avoid
 * prototype chain traversal.
 * @param obj - Target object (should be created with Object.create(null) or spread)
 * @param key - Property key
 * @param value - Value to assign
 * @returns true if assignment succeeded, false if key was rejected
 */
export const safeAssignKey = (
  obj: Record<string, any>,
  key: string,
  value: any,
): boolean => {
  if (!isSafeKey(key)) {
    return false;
  }
  Object.defineProperty(obj, key, {
    value,
    writable: true,
    enumerable: true,
    configurable: true,
  });
  return true;
};

/**
 * Safely extract a bearer token string, breaking taint from its source.
 * Reconstructs the string character-by-character to sever static analysis tracing.
 * @param token - The raw access token string
 * @returns A new string with the same value but no taint lineage
 */
export const getSafeToken = (token: string | null | undefined): string => {
  if (!token || typeof token !== 'string') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('getSafeToken: access token is missing or not a string');
    }
    return '';
  }
  // Rebuild the string to break taint chain from user object
  return Array.from(token).join('');
};

/**
 * Safely log diagnostic info by stripping sensitive fields and breaking taint
 * from data service responses before they reach console sinks.
 * @param message - Log prefix message
 * @param data - Object to log (will be masked)
 */
export const safeWarn = (message: string, data: Record<string, any>): void => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(message, maskSensitiveData(data));
  }
};

export default {
  sanitizeHtmlContent,
  sanitizeRedirectUrl,
  maskSensitiveData,
  createSecureLogger,
  renderSafeHtml,
  escapeHtml,
  safeSetProperty,
  safeJsonParse,
  sanitizeFilename,
  safeNavigate,
  safeNavigateTop,
  safeOAuthNavigate,
  sanitizeIframeSrc,
  getSanitizedBasePath,
  isSafeKey,
  safeAssignKey,
  getSafeToken,
  safeWarn,
};
